import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Image, ImageRole } from '@frontend/core';
import { map, of } from 'rxjs';

/** A gallery pool: a layout `role` + one or more gallery `tags` (comma-joined). */
export interface Gallery {
  role: ImageRole;
  tags: string;
  data: Image[];
}

@Injectable({
  providedIn: 'root'
})
export class GalleryService {

  galleries : Gallery[] = [];

  constructor(private http : HttpClient) { }

  private normaliseTags (tags : string | string[]) : string {
    return Array.isArray(tags) ? tags.join(',') : tags;
  }

  /**
   * @param role  the layout role of the pool (`banner` or `hero`).
   * @param tags  the gallery pool tag(s) — a single slug or a comma-separated list
   *   (e.g. `general`, `profile-banner`, `snapshot`, or `snapshot,nation`).
   */
  getGallery (role : ImageRole, tags : string | string[]) {

    const tagsParam = this.normaliseTags(tags);

    let cachedGallery : Gallery = this.getCachedGallery(role, tagsParam);
    if (cachedGallery){
      return of(cachedGallery);
    };

    let url = 'api/v1/media/gallery';
    let searchParams = new HttpParams();
    searchParams = searchParams.append('role', role);
    searchParams = searchParams.append('tagSlugs', tagsParam);
    url = `${url}?${searchParams.toString()}`;

    return this.http.get<{'data' : Image[]}>(url)
        .pipe(
          map(response =>{
            let gallery : Gallery = { role, tags: tagsParam, data: response.data };
            this.cacheGallery(gallery);
            return gallery;
          })
        )
  };

  getCachedGallery (role : ImageRole, tags : string) : Gallery {
    if (!this.galleries.length){
      return null;
    }
    let cachedGallery = this.galleries.find(g=>g.role===role && g.tags===tags);
    if (cachedGallery?.data?.length){
      return cachedGallery;
    } else {
      return null;
    }
  };

  cacheGallery (gallery : Gallery){
    let index = this.galleries.findIndex(g=>g.role===gallery.role && g.tags===gallery.tags);
    if (index >-1){
      this.galleries[index]= gallery;
    } else {
      this.galleries.push(gallery);
    }
  };

}
